import React from 'react'
import { observer } from 'mobx-react-lite'
import CheckBox from 'shared/ui/CheckBox/CheckBox'
import InitialAvatar from 'shared/components/InitialAvatar/InitialAvatar'
import PositionedTooltip from 'shared/components/PositionedTooltip/PositionedTooltip'
import { normalizeApplicationStatusKey } from 'pages/ApplicationDetailsPage/utils/applicationStatusUi'
import { essaySummaryStore } from '../../model/essaySummaryStore'
import AttentionCell from '../AttentionCell/AttentionCell'
import DeadlinePill from '../DeadlinePill/DeadlinePill'
import ExpandedGrid from '../ExpandedGrid/ExpandedGrid'
import LoaderImg from 'shared/components/LoaderImg/LoaderImg'
import s from './ApplicationRow.module.scss'
import { ReactComponent as ChevronIcon } from 'assets/icons/arrow_short_right.svg'

const normalizeIso2 = value => {
	const v = String(value || '')
		.trim()
		.toUpperCase()
	if (!/^[A-Z]{2}$/.test(v)) return null
	return v
}

const ApplicationRow = observer(
	({
		row,
		selected = false,
		expanded = false,
		onToggleSelect,
		onToggleExpand,
		onOpenStudent,
		onOpenApplication,
		onAssignAssignment,
		onGenerateLor,
		onOpenEssay,
		showSelect = true,
		showStudent = true,
		isLoading = false,
	}) => {
		if (isLoading) {
			return (
				<article className={`${s.card} ${s.loading}`}>
					<div className={s.row}>
						{showSelect && <div className={s.check} />}

						{showStudent && (
							<div className={s.student}>
								<span className={`${s.loader} ${s.loaderAvatar}`}>
									<LoaderImg />
								</span>
								<span className={`${s.loader} ${s.loaderName}`}>
									<LoaderImg />
								</span>
							</div>
						)}

						<div className={s.uni}>
							<span className={`${s.loader} ${s.loaderName}`}>
								<LoaderImg />
							</span>
							<span className={`${s.loader} ${s.loaderSub}`}>
								<LoaderImg />
							</span>
						</div>

						<div className={s.attention}>
							<span className={`${s.loader} ${s.loaderCell}`}>
								<LoaderImg />
							</span>
						</div>

						<div className={s.deadline}>
							<span className={`${s.loader} ${s.loaderCell}`}>
								<LoaderImg />
							</span>
						</div>

						<span className={`${s.loader} ${s.loaderChevron}`}>
							<LoaderImg />
						</span>
					</div>
				</article>
			)
		}

		if (!row) return null

		const statusKey = normalizeApplicationStatusKey(row.applicationStatus)
		const iso2 = normalizeIso2(row.country)
		const essayLoading =
			essaySummaryStore.loadStatus === 'loading' ||
			essaySummaryStore.loadStatus === 'idle'

		const handleStudent = e => {
			e.stopPropagation()
			onOpenStudent?.(row)
		}

		const handleExpand = () => {
			onToggleExpand?.(row.id)
		}

		return (
			<article
				className={`${s.card} ${expanded ? s.expanded : ''} ${s[`status_${statusKey}`] || ''}`}
			>
				<div className={s.row} onClick={handleExpand}>
					{showSelect && (
						<div className={s.check} onClick={e => e.stopPropagation()}>
							<CheckBox
								isSelected={selected}
								onToggle={() => onToggleSelect?.(row.id)}
							/>
						</div>
					)}

					{showStudent && (
						<PositionedTooltip
							content="Open application"
							className={s.studentTooltip}
						>
							<button
								type="button"
								className={s.student}
								onClick={handleStudent}
								aria-label="Open application"
							>
								<InitialAvatar
									name={row.studentName || '—'}
									size={28}
									studentId={row.studentId}
								/>
								<span className={s.studentName}>
									{row.studentName || '—'}
								</span>
							</button>
						</PositionedTooltip>
					)}

					<div className={s.uni}>
						<span className={s.uniName}>{row.university || '—'}</span>
						<span className={s.uniSub}>
							{row.major || '—'}
							{iso2 && <span className={s.uniCountry}> · {iso2}</span>}
						</span>
					</div>

					<div className={s.attention}>
						<AttentionCell
							essayStatus={row.essayAggregateStatus}
							feedbackCount={Number(row.feedbackCount) || 0}
							docsCount={Number(row.docsUpdatesCount) || 0}
							isNewApplication={Boolean(row.isNewApplication)}
							essayLoading={essayLoading}
							hasPendingStudentLorRequest={Boolean(
								row.hasPendingStudentLorRequest,
							)}
						/>
					</div>

					<div className={s.deadline}>
						<DeadlinePill deadline={row.deadline} />
					</div>

					<button
						type="button"
						className={`${s.chevron} ${expanded ? s.chevronOpen : ''}`}
						onClick={e => {
							e.stopPropagation()
							handleExpand()
						}}
						aria-label={expanded ? 'Collapse' : 'Expand'}
					>
						<span className={s.chevron__icon}>
							<ChevronIcon />
						</span>
					</button>
				</div>

				{expanded && (
					<ExpandedGrid
						row={row}
						onOpenApplication={onOpenApplication}
						onAssignAssignment={onAssignAssignment}
						onGenerateLor={onGenerateLor}
						onOpenEssay={onOpenEssay}
					/>
				)}
			</article>
		)
	},
)

export default ApplicationRow
